import axios, { AxiosInstance, AxiosError } from 'axios';
import ssoService from './ssoService';

const API_BASE_URL = process.env.API_BASE_URL || 'http://localhost:3001';

export type ApiErrorType = 'AUTH_FAILED' | 'FORBIDDEN' | 'EXPIRED' | 'NETWORK' | 'SERVER_ERROR';

export interface ApiError extends Error {
  type: ApiErrorType;
  code?: number | string;
  originalError?: any;
}

// 60s leeway mirrors backend graphService.ts so the frontend never hands out a
// token that's about to expire mid-flight.
const GRAPH_TOKEN_LEEWAY_MS = 60_000;

/**
 * API Client configuration
 * Now uses Azure AD tokens obtained via Office.js SSO
 */
class ApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  // In-flight request dedup: collapses concurrent calls to the same endpoint
  // (e.g. rapid client switches, StrictMode double-renders) into one promise.
  // Entries are removed in .finally() so cached results don't survive completion.
  private inflightRequests: Map<string, Promise<any>> = new Map();

  // In-memory Graph token cache — avoids hitting localStorage on every read
  // and mirrors the SSO service's token-cache pattern.
  private cachedGraphToken: { token: string; expiresAt: number } | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}/api`,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add Azure AD auth token
    this.client.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest: any = error.config;

        // If 401 and we haven't retried yet, try to refresh token
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Get fresh token from SSO service
            const newToken = await ssoService.getAccessToken(true);
            this.setToken(newToken);

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError: any) {
            console.error('Token refresh failed:', refreshError);
            this.clearToken();
            // Create structured error for expired token
            const structuredError = new Error(
              refreshError.message || 'Your session has expired. Please sign in again.'
            ) as ApiError;
            structuredError.type = 'EXPIRED';
            structuredError.code = 401;
            structuredError.originalError = refreshError;
            return Promise.reject(structuredError);
          }
        }

        // If 403, user might be from wrong tenant
        if (error.response?.status === 403) {
          console.error('Access forbidden - possibly wrong tenant');
          this.clearToken();
          const errorMessage = (error.response?.data as any)?.message || 
                              'You do not have permission to access this add-in. This add-in is only available for members of the authorized organization.';
          const structuredError = new Error(errorMessage) as ApiError;
          structuredError.type = 'FORBIDDEN';
          structuredError.code = 403;
          structuredError.originalError = error;
          return Promise.reject(structuredError);
        }

        // Handle network errors
        if (!error.response) {
          const structuredError = new Error(
            'Network error. Please check your internet connection and try again.'
          ) as ApiError;
          structuredError.type = 'NETWORK';
          structuredError.originalError = error;
          return Promise.reject(structuredError);
        }

        // Handle other server errors
        if (error.response?.status >= 500) {
          const structuredError = new Error(
            (error.response?.data as any)?.message || 'Server error. Please try again later.'
          ) as ApiError;
          structuredError.type = 'SERVER_ERROR';
          structuredError.code = error.response.status;
          structuredError.originalError = error;
          return Promise.reject(structuredError);
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Set Azure AD authentication token
   */
  setToken(token: string): void {
    this.token = token;
    // Token is already stored by ssoService, but keep this reference
  }

  /**
   * Get stored Azure AD token
   */
  getToken(): string | null {
    if (!this.token) {
      // Try to get from SSO service cache
      this.token = ssoService.getCachedToken();
    }
    return this.token;
  }

  /**
   * Clear authentication token
   */
  clearToken(): void {
    this.token = null;
    this.cachedGraphToken = null;
    ssoService.clearToken();
    localStorage.removeItem('graph_token');
    localStorage.removeItem('graph_token_expires');
  }

  /**
   * Set Microsoft Graph token
   */
  setGraphToken(token: string, expiresOn: number): void {
    this.cachedGraphToken = { token, expiresAt: expiresOn };
    localStorage.setItem('graph_token', token);
    localStorage.setItem('graph_token_expires', expiresOn.toString());
  }

  /**
   * Get Microsoft Graph token. Checks the in-memory cache first, then falls
   * back to localStorage (and hydrates the cache from it).
   */
  getGraphToken(): string | null {
    const now = Date.now();

    if (this.cachedGraphToken && this.cachedGraphToken.expiresAt - GRAPH_TOKEN_LEEWAY_MS > now) {
      return this.cachedGraphToken.token;
    }
    // In-memory entry expired — drop it before falling back to storage.
    this.cachedGraphToken = null;

    const token = localStorage.getItem('graph_token');
    const expiresOn = localStorage.getItem('graph_token_expires');

    if (token && expiresOn) {
      const expiryTime = parseInt(expiresOn, 10);
      if (expiryTime - GRAPH_TOKEN_LEEWAY_MS > now) {
        this.cachedGraphToken = { token, expiresAt: expiryTime };
        return token;
      }
    }

    return null;
  }

  /**
   * Collapse concurrent calls with the same key onto a single in-flight
   * promise. Entry is removed when the promise settles.
   */
  private dedupe<T>(key: string, fn: () => Promise<T>): Promise<T> {
    const existing = this.inflightRequests.get(key) as Promise<T> | undefined;
    if (existing) return existing;
    const promise = fn().finally(() => {
      this.inflightRequests.delete(key);
    });
    this.inflightRequests.set(key, promise);
    return promise;
  }

  /**
   * Initialize Graph client - fetch and store Microsoft Graph token
   */
  async initializeGraphClient(): Promise<{ success: boolean; token?: string; message?: string }> {
    try {
      // Check if we already have a valid token
      const existingToken = this.getGraphToken();
      if (existingToken) {
        return { success: true, token: existingToken };
      }

      // Fetch new token from backend
      const response = await this.client.get('/auth/graph-token');
      
      if (response.data.success && response.data.token) {
        this.setGraphToken(response.data.token, response.data.expiresOn);
        return { success: true, token: response.data.token };
      }
      
      return { success: false, message: 'Failed to fetch Graph token' };
    } catch (error: any) {
      console.error('Error initializing Graph client:', error);
      
      // If it's a 401 or auth-related error, re-throw it so App.tsx can handle it
      if (error.type === 'EXPIRED' || error.type === 'FORBIDDEN' || error.type === 'AUTH_FAILED') {
        throw error;
      }
      
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to initialize Graph client' 
      };
    }
  }

  /**
   * Initialize authentication using Azure AD SSO
   * This replaces the old email/password login
   */
  async initializeAuth(): Promise<{ success: boolean; token?: string; error?: string }> {
    try {
      // Get Azure AD token via SSO
      const token = await ssoService.getAccessToken();
      this.setToken(token);

      // Decode token to get user info (for display only)
      const userInfo = ssoService.decodeToken(token);

      return { success: true, token };
    } catch (error: any) {
      console.error('Authentication failed:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to authenticate with Azure AD'
      };
    }
  }

  /**
   * @deprecated Use initializeAuth() instead
   * Kept for backward compatibility but no longer functional
   */
  async login(email: string, password: string): Promise<any> {
    throw new Error('Email/password login is deprecated. The add-in now uses Azure AD SSO.');
  }

  /**
   * Match sender email with clients
   */
  async matchClient(senderEmail: string): Promise<any> {
    const response = await this.client.post('/email/match-client', { senderEmail });
    return response.data;
  }

  /**
   * Save email with attachments
   */
  async saveEmail(data: {
    clientId: number;
    email: any;
    attachments: any[];
  }): Promise<any> {
    const response = await this.client.post('/email/save', data);
    return response.data;
  }

  /**
   * Get all clients
   */
  async getClients(): Promise<any> {
    const response = await this.client.get('/clients');
    return response.data;
  }

  /**
   * Get klanten (customers) by email ID. Deduped against concurrent identical
   * calls (e.g. React StrictMode's double-effect or rapid email switches).
   */
  async getKlantenByEmail(email: string): Promise<any> {
    return this.dedupe(`klanten:email:${email}`, async () => {
      const response = await this.client.post('/klanten/listklantenbyemailid', { email });
      return response.data;
    });
  }

  /**
   * Search klanten by name or number for the manual "Aangepast" lookup.
   * Pass an AbortSignal to cancel an in-flight request when the query changes.
   */
  async searchKlanten(q: string, limit: number = 20, signal?: AbortSignal): Promise<any> {
    const response = await this.client.get('/klanten/search', {
      params: { q, limit },
      signal,
    });
    return response.data;
  }

  /**
   * Save email with attachments to klanten
   */
  async saveEmailAttachments(data: {
    clientId: number;
    email: any;
    attachment: any[];
    customFolder?: string;
    isOutgoing?: boolean;
    saveEmail?: boolean;
    saveAttachments?: boolean;
    saveEmailFile?: boolean;
    restMessageId?: string;
    userPrincipalName?: string;
  }): Promise<any> {
    const response = await this.client.post('/klanten/saveemailattachments', data);
    return response.data;
  }

  /**
   * Get emails for a client
   */
  async getClientEmails(clientId: number, page: number = 1): Promise<any> {
    const response = await this.client.get(`/email/client/${clientId}`, {
      params: { page },
    });
    return response.data;
  }

  /**
   * Get SharePoint folder path for a client. Deduped — rapid client switches
   * or a refresh-while-loading would otherwise hit Graph twice via the backend.
   */
  async getClientSharePointPath(clientId: number): Promise<any> {
    return this.dedupe(`sp:path:${clientId}`, async () => {
      const response = await this.client.get(`/klanten/sharepointpath/${clientId}`);
      return response.data;
    });
  }

  /**
   * Get available SharePoint folders for a client. Deduped (see above).
   */
  async getClientSharePointFolders(clientId: number): Promise<any> {
    return this.dedupe(`sp:folders:${clientId}`, async () => {
      const response = await this.client.get(`/klanten/sharepointfolders/${clientId}`);
      return response.data;
    });
  }

  /**
   * Create a new SharePoint folder under an arbitrary parent path
   */
  async createSharePointFolder(
    clientId: number,
    parentPath: string,
    folderName: string
  ): Promise<any> {
    const response = await this.client.post(
      `/klanten/sharepointfolders/${clientId}`,
      { parentPath, folderName }
    );
    return response.data;
  }

  /**
   * Rename an existing SharePoint folder
   */
  async renameSharePointFolder(
    clientId: number,
    folderPath: string,
    newName: string
  ): Promise<any> {
    const response = await this.client.patch(
      `/klanten/sharepointfolders/${clientId}/rename`,
      { folderPath, newName }
    );
    return response.data;
  }

  /**
   * Move an existing SharePoint folder under a different parent folder
   */
  async moveSharePointFolder(
    clientId: number,
    folderPath: string,
    newParentPath: string
  ): Promise<any> {
    const response = await this.client.patch(
      `/klanten/sharepointfolders/${clientId}/move`,
      { folderPath, newParentPath }
    );
    return response.data;
  }

  /**
   * Delete an existing SharePoint folder (and its contents)
   */
  async deleteSharePointFolder(
    clientId: number,
    folderPath: string
  ): Promise<any> {
    const response = await this.client.delete(
      `/klanten/sharepointfolders/${clientId}`,
      { data: { folderPath } }
    );
    return response.data;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
